import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function AddCourseModal({ onClose, onSubmit, schools = [] }) {
  const [courseData, setCourseData] = useState({
    courseName: '',
    grade: '',
    credits: '5.0',
    courseLevel: 'Regular',
    gradeLevel: '9',
    term: '1',
    institution: 'DVHS'
  });

  const [errors, setErrors] = useState({});

  const gradeOptions = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'D-', 'F'];
  const courseLevelOptions = ['Regular', 'Honors', 'AP'];
  const gradeLevelOptions = ['9', '10', '11', '12'];
  const termOptions = ['1', '2', '3'];
  
  // Build institution options from passed schools prop
  const institutionOptions = ['DVHS', ...schools.filter(s => s !== 'DVHS')];

  // Add escape key handler and prevent body scroll on mobile
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    // Prevent body scroll when modal is open (mobile fix)
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const validate = () => {
    const newErrors = {};
    
    if (!courseData.courseName.trim()) {
      newErrors.courseName = 'Course name is required';
    }
    
    if (!courseData.grade) {
      newErrors.grade = 'Grade is required';
    }
    
    const credits = parseFloat(courseData.credits);
    if (isNaN(credits) || credits <= 0 || credits > 20) {
      newErrors.credits = 'Credits must be between 0 and 20';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validate()) {
      onSubmit({
        ...courseData,
        credits: parseFloat(courseData.credits)
      });
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 sm:p-4"
      onClick={(e) => {
        // Close modal when clicking backdrop (not on mobile due to full screen)
        if (e.target === e.currentTarget && window.innerWidth >= 640) {
          onClose();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-900 rounded-none sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900 z-10">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Add Missing Course</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 -mr-2"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Info Banner */}
          <div className="p-4 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <strong>Note:</strong> This course will be added to your transcript and included in GPA calculations. 
              It will follow DVHS standards and be treated according to the course level you select.
            </p>
          </div>

          {/* Course Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Course Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={courseData.courseName}
              onChange={(e) => setCourseData({ ...courseData, courseName: e.target.value })}
              className="w-full px-4 py-3 sm:py-2.5 text-base bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-offset-0 transition-all"
              style={{ '--tw-ring-color': 'var(--accent-500)' }}
              placeholder="e.g., AP Calculus BC"
            />
            {errors.courseName && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.courseName}</p>
            )}
          </div>

          {/* Grid for smaller fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Grade */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Grade Received <span className="text-red-500">*</span>
              </label>
              <select
                value={courseData.grade}
                onChange={(e) => setCourseData({ ...courseData, grade: e.target.value })}
                className="w-full px-4 py-3 sm:py-2.5 text-base bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-offset-0 transition-all"
                style={{ '--tw-ring-color': 'var(--accent-500)' }}
              >
                <option value="">Select Grade</option>
                {gradeOptions.map(grade => (
                  <option key={grade} value={grade}>{grade}</option>
                ))}
              </select>
              {errors.grade && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.grade}</p>
              )}
            </div>

            {/* Course Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Course Level <span className="text-red-500">*</span>
              </label>
              <select
                value={courseData.courseLevel}
                onChange={(e) => setCourseData({ ...courseData, courseLevel: e.target.value })}
                className="w-full px-4 py-3 sm:py-2.5 text-base bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-offset-0 transition-all"
                style={{ '--tw-ring-color': 'var(--accent-500)' }}
              >
                {courseLevelOptions.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            {/* Credits */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Credits <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.5"
                value={courseData.credits}
                onChange={(e) => setCourseData({ ...courseData, credits: e.target.value })}
                className="w-full px-4 py-3 sm:py-2.5 text-base bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-offset-0 transition-all"
                style={{ '--tw-ring-color': 'var(--accent-500)' }}
              />
              {errors.credits && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.credits}</p>
              )}
            </div>

            {/* Grade Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Grade Level <span className="text-red-500">*</span>
              </label>
              <select
                value={courseData.gradeLevel}
                onChange={(e) => setCourseData({ ...courseData, gradeLevel: e.target.value })}
                className="w-full px-4 py-3 sm:py-2.5 text-base bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-offset-0 transition-all"
                style={{ '--tw-ring-color': 'var(--accent-500)' }}
              >
                {gradeLevelOptions.map(level => (
                  <option key={level} value={level}>Grade {level}</option>
                ))}
              </select>
            </div>

            {/* Term */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Term <span className="text-red-500">*</span>
              </label>
              <select
                value={courseData.term}
                onChange={(e) => setCourseData({ ...courseData, term: e.target.value })}
                className="w-full px-4 py-3 sm:py-2.5 text-base bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-offset-0 transition-all"
                style={{ '--tw-ring-color': 'var(--accent-500)' }}
              >
                {termOptions.map(term => (
                  <option key={term} value={term}>Term {term}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Institution Selection */}
          {institutionOptions.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Institution <span className="text-red-500">*</span>
              </label>
              <select
                value={courseData.institution}
                onChange={(e) => setCourseData({ ...courseData, institution: e.target.value })}
                className="w-full px-4 py-3 sm:py-2.5 text-base bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-lg text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-offset-0 transition-all"
                style={{ '--tw-ring-color': 'var(--accent-500)' }}
              >
                {institutionOptions.map(institution => (
                  <option key={institution} value={institution}>{institution}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select the institution where this course was taken.
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-4 sticky bottom-0 bg-white dark:bg-gray-900 pb-4 sm:pb-0 sm:static -mx-4 sm:mx-0 px-4 sm:px-0 border-t sm:border-t-0 border-gray-200 dark:border-gray-800 sm:border-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 sm:py-2.5 text-base bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 sm:py-2.5 text-base text-white rounded-lg font-medium transition-all"
              style={{
                background: 'linear-gradient(to right, var(--accent-500), var(--accent-600))'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to right, var(--accent-600), var(--accent-700))';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(to right, var(--accent-500), var(--accent-600))';
              }}
            >
              Add Course
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
